/* PreNumbers Critical Finance — Entities (reskinned).

   Logic (add / save / delete / select, industry & currency cascades) is
   unchanged from the original — only the markup is reskinned to the .pn-*
   design system. EntityDetails is still exported (the Dashboard renders it
   read-only). */

import { useState, useContext } from 'react';

import { genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';
import { CriticalFinancePageContext } from '../CriticalFiancePage.jsx';
import { industryDetails, entityRenderFields } from '../../constants.jsx';
import { renderFbField, PageHead, StatTile, FbIcons } from '../../FinancialBuilder/fbUI.jsx';
import { ApartmentOutlined, CheckOutlined, DollarOutlined, DownOutlined, LineChartOutlined } from '@ant-design/icons';

const { ArrowRightOutlined, PlusOutlined, DeleteOutlined, SaveOutlined } = FbIcons;

async function addEntityToDatabase(entity) {
  return genericPOSTDatabaseCall('/api/cfi/addEntity', { entity }, 'entity');
}
async function saveEntityToDatabase(entity) {
  return genericPOSTDatabaseCall('/api/cfi/saveEntity', { entity }, 'entity info');
}
async function deleteEntityFromDatabase(entityId) {
  return genericDELETEDatabaseCall('/api/cfi/deleteEntity', { entityId }, 'entity');
}

/* Renders the entity's fields (+ actions when editable). No card chrome — the
   caller supplies it (a stack card here, a ShowAndHide panel in the Dashboard). */
export function EntityDetails({ entity, setEntities, messageApi, editable = true, setEntitySelectedID, spotRates }) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const saveEntity = async (e) => {
    setSaving(true);
    messageApi.loading('Saving entity info...', 0);
    try {
      await saveEntityToDatabase(e);
      messageApi.destroy();
      messageApi.success('Entity info saved successfully');
    } catch (error) {
      console.error('Error saving entity info:', error);
      messageApi.destroy();
      messageApi.error('Failed to save entity info');
    }
    setSaving(false);
  };

  const deleteEntity = async (entityId) => {
    setDeleting(true);
    messageApi.loading('Deleting entity...', 0);
    try {
      await deleteEntityFromDatabase(entityId);
      setEntities(prevEntities => prevEntities.filter(ent => ent.id !== entityId));
      setEntitySelectedID(""); // Clear selected entity if it was deleted
      messageApi.destroy();
      messageApi.success('Entity deleted successfully');
    } catch (error) {
      console.error('Error deleting entity:', error);
      messageApi.destroy();
      messageApi.error('Failed to delete entity');
    }
    setDeleting(false);
  };

  const handleInputChange = (event, changedEntity, key) => {
    let newValue = event.target.value;
    const updatedEntityBase = { ...changedEntity, [key]: newValue };

    if (key === 'industry') {
      const keys = Object.keys(industryDetails[newValue]);
      for (const k of keys) {
        if (updatedEntityBase[k] === undefined) continue; // Skip if not defined for entity
        updatedEntityBase[k] = industryDetails[newValue][k];
      }
    }

    if (key === 'in_currency' || key === 'out_currency') {
      const conversionKey = updatedEntityBase.in_currency + '_' + updatedEntityBase.out_currency;
      updatedEntityBase.spot_rate = (spotRates && spotRates[conversionKey]) || '1.000';
    }

    setEntities(prevEntities => prevEntities.map(ent => (ent.id === updatedEntityBase.id ? updatedEntityBase : ent)));
  };

  const handleInputBlur = (event, changedEntity, key, field) => {
    let newValue = event.target.value;
    if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) {
      messageApi.error(field.errorMessage);
      newValue = field.default || '';
    }
    if (newValue === '') {
      messageApi.error('This field cannot be empty');
      newValue = field.default || '';
    }
    setEntities(prevEntities => prevEntities.map(ent => (ent.id === changedEntity.id ? { ...ent, [key]: newValue } : ent)));
  };

  return (
    <>
      <div className="pn-fields">
        {entityRenderFields.map(field => renderFbField(field, entity, handleInputChange, editable, handleInputBlur))}
        <div className="pn-field">
          <label>Spot Rate</label>
          <div className="pn-input-wrap"><input className="pn-control" value={entity.spot_rate ?? ''} readOnly /></div>
        </div>
      </div>
      {editable && (
        <div className="pn-editor-foot">
          <button type="button" className="pn-btn pn-btn-danger" disabled={deleting} onClick={() => deleteEntity(entity.id)}><DeleteOutlined /> Delete entity</button>
          <div className="sp" />
          <button type="button" className="pn-btn pn-btn-accent" disabled={saving} onClick={() => saveEntity(entity)}><SaveOutlined /> Save entity</button>
        </div>
      )}
    </>
  );
}

function EntityCard({ entity, isActive, onSelect, setEntities, messageApi, spotRates, setEntitySelectedID }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={"pn-stack-card" + (open ? " open" : "")}>
      <div className="pn-stack-head" onClick={() => setOpen((o) => !o)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span className="pn-radio" data-on={isActive ? "1" : "0"} title="Set as active entity"
            onClick={(e) => { e.stopPropagation(); onSelect(String(entity.id)); }}>{isActive && <i />}</span>
          <div style={{ minWidth: 0 }}>
            <div className="pn-stack-name">{entity.name || 'Untitled entity'}</div>
            <div className="pn-stack-meta"><span>{entity.industry}</span><span>·</span><span>{entity.years?.length || 0} years</span><span>·</span><span>{entity.out_currency}</span></div>
          </div>
        </div>
        <span className="pn-badge" style={isActive ? { background: 'var(--pn-accent-dim)', color: 'var(--pn-accent)' } : { background: 'var(--pn-tint-08)', color: 'var(--pn-fg-tertiary)' }}>{isActive ? 'Active entity' : 'Select'}</span>
        <span className="chev"><DownOutlined style={{ fontSize: 14 }} /></span>
      </div>
      {open && (
        <div className="pn-stack-body">
          <EntityDetails entity={entity} editable setEntities={setEntities} messageApi={messageApi} spotRates={spotRates} setEntitySelectedID={setEntitySelectedID} />
        </div>
      )}
    </div>
  );
}

function EntitiesPage() {
  const { entities: contextEntities, setEntities, goTo, messageApi, entitySelectedID, setEntitySelectedID, spotRates } = useContext(CriticalFinancePageContext);
  const [newEntityName, setNewEntityName] = useState('');

  const active = contextEntities.find((e) => String(e.id) === String(entitySelectedID));
  const totalYears = contextEntities.reduce((n, e) => n + (e.years?.length || 0), 0);

  const addEntity = async () => {
    if (!newEntityName) {
      messageApi.error('Please enter a entity name');
      return;
    }
    messageApi.loading('Adding entity...', 0);
    try {
      const newEntity = await addEntityToDatabase({ name: newEntityName });
      // Apply industry defaults before adding to state
      const keys = Object.keys(industryDetails[newEntity.industry]);
      for (const k of keys) {
        if (newEntity[k] === undefined) continue;
        newEntity[k] = industryDetails[newEntity.industry][k];
      }
      setEntities(prevEntities => [...prevEntities, newEntity]);
      setNewEntityName('');
      messageApi.destroy();
      messageApi.success('Entity added successfully');
    } catch (error) {
      console.error('Error adding entity:', error);
      messageApi.destroy();
      messageApi.error('Failed to add entity');
    }
  };

  return (
    <div className="pn-wrap">
      <PageHead eyebrow="Workspace" title="Entities"
        sub={<>Manage your modeling entities. Pick one as the <b>active entity</b> to build projections and valuations.</>}>
        <button type="button" className="pn-btn pn-btn-cta" disabled={!entitySelectedID || contextEntities.length === 0} onClick={() => goTo(2)}>
          {entitySelectedID ? "Continue to Projections" : "Select an Entity"} {entitySelectedID && <ArrowRightOutlined />}
        </button>
      </PageHead>

      <div className="pn-stats">
        <StatTile icon={<ApartmentOutlined />} k="Entities" v={contextEntities.length} />
        <StatTile icon={<LineChartOutlined />} k="Projection years" v={totalYears} />
        <StatTile icon={<CheckOutlined />} k="Active entity" v={active ? active.name : '—'} />
        <StatTile icon={<DollarOutlined />} k="Currency" v={active ? active.out_currency : '—'} />
      </div>

      <div className="pn-stack">
        {contextEntities.map((e) => (
          <EntityCard key={e.id} entity={e} isActive={String(e.id) === String(entitySelectedID)}
            onSelect={setEntitySelectedID} setEntities={setEntities} messageApi={messageApi}
            spotRates={spotRates} setEntitySelectedID={setEntitySelectedID} />
        ))}
      </div>

      <div className="pn-addrow">
        <div className="pn-input-wrap" style={{ flex: 1 }}>
          <input className="pn-control" placeholder="New entity name" value={newEntityName}
            onChange={(e) => setNewEntityName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addEntity()} />
        </div>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={addEntity}><PlusOutlined /> Add entity</button>
      </div>
    </div>
  );
}

export default EntitiesPage;
